import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { priceService } from '../../services/priceService';
import { regionService } from '../../services/regionService';
import { vehicleService } from '../../services/vehicleService';
import { carrierService } from '../../services/carrierService';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';

const PriceBulkUpdate = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [regions, setRegions] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [prices, setPrices] = useState([
    {
      carrierId: '',
      fromRegionId: '',
      toRegionId: '',
      vehicleTypeId: '',
      price: '',
      currency: 'EUR',
      validFrom: '',
      validTo: ''
    }
  ]);

  useEffect(() => {
    fetchRegions();
    fetchVehicleTypes();
    fetchCarriers();
  }, []);

  const fetchRegions = async () => {
    try {
      const response = await regionService.getRegions();
      if (response && response.success && response.data) {
        setRegions(response.data.regions);
      }
    } catch (error) {
      console.error('Error fetching regions:', error);
      toast.error('Bölgeler yüklenirken bir hata oluştu');
    }
  };

  const fetchVehicleTypes = async () => {
    try {
      const response = await vehicleService.getVehicleTypes();
      if (response && response.success && response.data) {
        setVehicleTypes(response.data.vehicleTypes);
      }
    } catch (error) {
      console.error('Error fetching vehicle types:', error);
      toast.error('Araç tipleri yüklenirken bir hata oluştu');
    }
  };

  const fetchCarriers = async () => {
    try {
      const response = await carrierService.getCarriers({ isActive: true });
      if (response && response.success && response.data && response.data.rows) {
        setCarriers(response.data.rows);
      }
    } catch (error) {
      console.error('Error fetching carriers:', error);
      toast.error('Taşıyıcılar yüklenirken bir hata oluştu');
    }
  };

  const handleAddPrice = () => {
    setPrices([
      ...prices,
      {
        carrierId: '',
        fromRegionId: '',
        toRegionId: '',
        vehicleTypeId: '',
        price: '',
        currency: 'EUR',
        validFrom: '',
        validTo: ''
      }
    ]);
  };

  const handleRemovePrice = (index) => {
    const newPrices = [...prices];
    newPrices.splice(index, 1);
    setPrices(newPrices);
  };

  const handlePriceChange = (index, field, value) => {
    const newPrices = [...prices];
    newPrices[index][field] = value;
    setPrices(newPrices);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validasyon
    const errors = [];
    prices.forEach((price, index) => {
      if (!price.carrierId) errors.push(`Satır ${index + 1}: Taşıyıcı seçimi zorunludur`);
      if (!price.fromRegionId) errors.push(`Satır ${index + 1}: Başlangıç bölgesi seçimi zorunludur`);
      if (!price.toRegionId) errors.push(`Satır ${index + 1}: Varış bölgesi seçimi zorunludur`);
      if (!price.vehicleTypeId) errors.push(`Satır ${index + 1}: Araç tipi seçimi zorunludur`);
      if (!price.price) errors.push(`Satır ${index + 1}: Fiyat zorunludur`);
      if (price.validFrom && price.validTo && new Date(price.validFrom) > new Date(price.validTo)) {
        errors.push(`Satır ${index + 1}: Geçerlilik bitiş tarihi, başlangıç tarihinden sonra olmalıdır`);
      }
    });

    if (errors.length > 0) {
      errors.forEach(error => toast.error(error));
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await priceService.bulkUpdatePrices({ prices });
      
      if (response && response.success) {
        toast.success('Fiyatlar başarıyla kaydedildi');
        navigate('/prices');
      } else {
        toast.error(response?.message || 'Fiyatlar kaydedilirken bir hata oluştu');
      }
    } catch (error) {
      console.error('Error saving prices:', error);
      toast.error(error.response?.data?.message || 'Fiyatlar kaydedilirken bir hata oluştu');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopyPrevious = (index) => {
    if (index === 0) return;
    
    const newPrices = [...prices];
    const prevPrice = { ...newPrices[index - 1] };
    
    // Fiyat ve tarih bilgilerini kopyalama
    newPrices[index] = {
      ...newPrices[index],
      carrierId: prevPrice.carrierId,
      fromRegionId: prevPrice.fromRegionId,
      toRegionId: prevPrice.toRegionId,
      vehicleTypeId: prevPrice.vehicleTypeId,
      currency: prevPrice.currency
    };
    
    setPrices(newPrices);
  };

  return (
    <div>
      <div className="pb-5 border-b border-gray-200">
        <h1 className="text-2xl font-semibold text-gray-900">Toplu Fiyat Girişi</h1>
        <p className="mt-2 text-sm text-gray-500">
          Birden fazla fiyat eklemek veya güncellemek için bu formu kullanabilirsiniz.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="mt-6">
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <div className="px-4 py-5 sm:p-6">
            <div className="grid grid-cols-1 gap-6">
              {prices.map((price, index) => (
                <div key={index} className="border border-gray-200 rounded-md p-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium text-gray-900">Fiyat #{index + 1}</h3>
                    <div className="flex space-x-2">
                      {index > 0 && (
                        <button
                          type="button"
                          onClick={() => handleCopyPrevious(index)}
                          className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        >
                          Öncekini Kopyala
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => handleRemovePrice(index)}
                        className="inline-flex items-center p-1.5 border border-transparent rounded-full shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        <TrashIcon className="h-4 w-4" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                    <div className="sm:col-span-3">
                      <label htmlFor={`carrierId-${index}`} className="block text-sm font-medium text-gray-700">
                        Taşıyıcı *
                      </label>
                      <div className="mt-1">
                        <select
                          id={`carrierId-${index}`}
                          value={price.carrierId}
                          onChange={(e) => handlePriceChange(index, 'carrierId', e.target.value)}
                          className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                          required
                        >
                          <option value="">Seçiniz</option>
                          {carriers.map(carrier => (
                            <option key={carrier.id} value={carrier.id}>{carrier.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="sm:col-span-3">
                      <label htmlFor={`vehicleTypeId-${index}`} className="block text-sm font-medium text-gray-700">
                        Araç Tipi *
                      </label>
                      <div className="mt-1">
                        <select
                          id={`vehicleTypeId-${index}`}
                          value={price.vehicleTypeId}
                          onChange={(e) => handlePriceChange(index, 'vehicleTypeId', e.target.value)}
                          className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                          required
                        >
                          <option value="">Seçiniz</option>
                          {vehicleTypes.map(type => (
                            <option key={type.id} value={type.id}>{type.name} (Kapasite: {type.capacity})</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="sm:col-span-3">
                      <label htmlFor={`fromRegionId-${index}`} className="block text-sm font-medium text-gray-700">
                        Başlangıç Bölgesi *
                      </label>
                      <div className="mt-1">
                        <select
                          id={`fromRegionId-${index}`}
                          value={price.fromRegionId}
                          onChange={(e) => handlePriceChange(index, 'fromRegionId', e.target.value)}
                          className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                          required
                        >
                          <option value="">Seçiniz</option>
                          {regions.map(region => (
                            <option key={region.id} value={region.id}>{region.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="sm:col-span-3">
                      <label htmlFor={`toRegionId-${index}`} className="block text-sm font-medium text-gray-700">
                        Varış Bölgesi *
                      </label>
                      <div className="mt-1">
                        <select
                          id={`toRegionId-${index}`}
                          value={price.toRegionId}
                          onChange={(e) => handlePriceChange(index, 'toRegionId', e.target.value)}
                          className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                          required
                        >
                          <option value="">Seçiniz</option>
                          {regions.map(region => (
                            <option key={region.id} value={region.id}>{region.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="sm:col-span-2">
                      <label htmlFor={`price-${index}`} className="block text-sm font-medium text-gray-700">
                        Fiyat *
                      </label>
                      <div className="mt-1">
                        <input
                          type="number"
                          id={`price-${index}`}
                          value={price.price}
                          onChange={(e) => handlePriceChange(index, 'price', e.target.value)}
                          className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                          step="0.01"
                          min="0"
                          required
                        />
                      </div>
                    </div>

                    <div className="sm:col-span-2">
                      <label htmlFor={`currency-${index}`} className="block text-sm font-medium text-gray-700">
                        Para Birimi *
                      </label>
                      <div className="mt-1">
                        <select
                          id={`currency-${index}`}
                          value={price.currency}
                          onChange={(e) => handlePriceChange(index, 'currency', e.target.value)}
                          className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                          required
                        >
                          <option value="EUR">EUR</option>
                          <option value="USD">USD</option>
                          <option value="TRY">TRY</option>
                          <option value="GBP">GBP</option>
                        </select>
                      </div>
                    </div>

                    <div className="sm:col-span-3">
                      <label htmlFor={`validFrom-${index}`} className="block text-sm font-medium text-gray-700">
                        Geçerlilik Başlangıcı
                      </label>
                      <div className="mt-1">
                        <input
                          type="date"
                          id={`validFrom-${index}`}
                          value={price.validFrom}
                          onChange={(e) => handlePriceChange(index, 'validFrom', e.target.value)}
                          className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        />
                      </div>
                    </div>

                    <div className="sm:col-span-3">
                      <label htmlFor={`validTo-${index}`} className="block text-sm font-medium text-gray-700">
                        Geçerlilik Bitişi
                      </label>
                      <div className="mt-1">
                        <input
                          type="date"
                          id={`validTo-${index}`}
                          value={price.validTo}
                          onChange={(e) => handlePriceChange(index, 'validTo', e.target.value)}
                          className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4">
              <button
                type="button"
                onClick={handleAddPrice}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <PlusIcon className="-ml-1 mr-2 h-5 w-5 text-gray-400" aria-hidden="true" />
                Yeni Fiyat Ekle
              </button>
            </div>
          </div>

          <div className="px-4 py-3 bg-gray-50 text-right sm:px-6">
            <button
              type="button"
              onClick={() => navigate('/prices')}
              className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 mr-3"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className={`inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${
                isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isSubmitting ? 'Kaydediliyor...' : 'Kaydet'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default PriceBulkUpdate;
